import _get from "lodash/get";
import _orderBy from "lodash/orderBy";
import _includes from "lodash/includes";
import {
    getArticleDomId,
    getArticleExcerptRaw,
    getArticleGroupingIdHashMap,
    getArticleLanguage,
    getArticleLanguageIsRightToLeft,
    getArticleOutletName,
    getArticleRawUrl,
    getArticleTitle,
    getArticleTitleRaw,
    getArticleUrl,
    getArticleAnalysisId,
    getArticleBestLicense,
    getArticleLicenseFeedUrl,
} from "./articles";
import { getTopHeaderOffset, isMobile } from "../../util/screen";
import { getAllDomArticlesMap } from "../../util/dom";

/**
 * This file contains redux selectors for accessing data that is related to navigation (e.g. contained in the URL, scroll etc)
 */

/**
 * Return the current article scroll for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getCurrentArticleScroll = (state, ownProps) => {
    return _get(state, "navigation.currentArticleScrollPercentage", 0);
};

/**
 * Return the initial hash
 * @param {*} state
 * @returns {*}
 */
export const getInitialHash = (state) => {
    return _get(state, "navigation.initialHash", "");
};

/**
 * Return the analysis id of the article currently being scrolled
 * @param {*} state
 * @param {*} ownProps
 * @returns {String|Number}
 */
export const getCurrentArticleScrollAnalysisId = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);

    return getArticleAnalysisId(state, id);
};

/**
 * Return the best license of the article currently being scrolled
 * @param {*} state
 * @param {*} ownProps
 * @returns {String}
 */
export const getCurrentArticleScrollBestLicense = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);

    return getArticleBestLicense(state, id);
};

/**
 * Return the license feed url of the article currently being scrolled
 * @param {*} state
 * @param {*} ownProps
 * @returns {String}
 */
export const getCurrentArticleScrollLicenseFeedUrl = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);

    return getArticleLicenseFeedUrl(state, id);
};

/**
 * Return the title of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollTitle = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const title = getArticleTitle(state, id);
    return title;
};

/**
 * Return the raw title of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollTitleRaw = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const title = getArticleTitleRaw(state, id);
    return title;
};

/**
 * Return the outlet name of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollOutlet = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const outlet = getArticleOutletName(state, id);
    return outlet;
};

/**
 * Return the excerptRaw name of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollExcerptRaw = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const excerpt = getArticleExcerptRaw(state, id);
    return excerpt;
};

/**
 * Return the DOM ID of the article currently being scrolled (actual ID)
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollDomId = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const domId = getArticleDomId(state, id);
    return domId;
};

/**
 * Return the article node of the article currently being scrolled (to be used for PDF)
 * @param state
 * @returns {HTMLElement}
 */
export const getCurrentArticleDomNode = (state, ownProps) => {
    const id = getCurrentArticleScrollDomId(state, ownProps);
    return document.getElementById(id);
};

/**
 * Return the url of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollUrl = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const url = getArticleUrl(state, id);
    return url;
};

/**
 * Return the raw url (no redirects) of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollRawUrl = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const url = getArticleRawUrl(state, id);
    return url;
};

/**
 * Return the language of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollLanguage = (state, ownProps) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    const language = getArticleLanguage(state, id);
    return language;
};

/**
 * Return the language of the article currently being scrolled
 * @param state
 * @returns {string}
 */
export const getCurrentArticleScrollLanguageisRightToLeft = (
    state,
    ownProps
) => {
    const id = getCurrentArticleIdBeingScrolled(state, ownProps);
    return getArticleLanguageIsRightToLeft(state, id);
};

/**
 * Is a PDF being downloaded (to determine which elements to show or hide)
 * @param state
 * @returns {boolean}
 */
export const getIsPdfDownloading = (state) => {
    return _get(state, "navigation.isPdfDownloading", false);
};

/**
 * Is something being copied to the clipboard
 * @param state
 * @returns {boolean}
 */
export const getIsClipboardCopying = (state) => {
    return _get(state, "navigation.isCopying", false);
};

/**
 * What are we copying to the clipboard
 * @param state
 * @returns {string}
 */
export const getCopyValue = (state) => {
    return _get(state, "navigation.thingToCopy", null);
};

/**
 * Calculate which article is currently being scrolled based on the DOM
 * @param state
 * @returns {{top, id: *, height}}
 */
export const calculateCurrentArticleBeingScrolled = (state) => {
    const allInDom = getAllDomArticlesMap();
    const sorted = _orderBy(allInDom, ["top"], ["desc"]);
    const offset = getTopHeaderOffset();
    const topThreshold = offset + 180; // Offset to make sure we are selecting the right article
    for (const element of sorted) {
        const { groupingId, top, height, fitsInScreen, domElement } = element;
        if (top < topThreshold) {
            const map = getArticleGroupingIdHashMap(state);
            const article = map[groupingId];
            const section = findClosestAncestorWithClass(
                domElement,
                "newsletter-section"
            );
            const sectionId = section ? section.dataset.sectionId : null;
            const res = {
                id: article.id,
                sectionId: sectionId,
                top,
                height,
                fitsInScreen,
            };
            return res;
        }
    }
    return {};
};

/**
 * Find the closest ancestor of element that contains the class name provided
 * @param {domElement} element
 * @param {string} className
 * @returns {*}
 */
export const findClosestAncestorWithClass = (element, className) => {
    let currentNode;
    for (
        let currentNode = element.parentElement;
        currentNode;
        currentNode = currentNode.parentElement
    ) {
        if (_includes([...currentNode.classList], className)) {
            return currentNode;
        }
    }

    return null;
};

/**
 * Return the current article being scrolled
 * @param state
 * @returns {*}
 */
export const getCurrentArticleBeingScrolled = (state, ownProps) => {
    return _get(state, "navigation.currentArticleBeingScrolled");
};

/**
 * Return the current article (raven) id being scrolled
 * @param state
 * @returns {*}
 */
export const getCurrentArticleIdBeingScrolled = (state, ownProps) => {
    const currentArticle = getCurrentArticleBeingScrolled(state, ownProps);
    return _get(currentArticle, "id");
};

/**
 * Return the current article (raven) id being scrolled
 * @param state
 * @returns {number}
 */
export const getCurrentSectionIdBeingScrolled = (state, ownProps) => {
    const currentArticle = getCurrentArticleBeingScrolled(state, ownProps);
    const sectionId = _get(currentArticle, "sectionId");
    if (sectionId) {
        try {
            return parseInt(sectionId);
        } catch (error) {
            return null;
        }
    }
    return null;
};

/**
 * Return the URL grouping id for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getUrlGroupingId = (state) => {
    return _get(state, "navigation.params.groupingId", "");
};

/**
 * Return the URL access key for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getUrlAccessKey = (state) => {
    return _get(state, "navigation.params.accessKey", "");
};

/**
 * Return the URL date for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getUrlDate = (state) => {
    return _get(state, "navigation.params.date", "");
};

/**
 * Return the current URL temporary ID
 * @param {*} state
 * @returns {*}
 */
export const getUrlTemporaryId = (state) => {
    return _get(state, "navigation.params.newsletterTempId", "");
};

/**
 * Is the sharing menu open
 * @param state
 * @returns {*}
 */
export const getIsSharingMenuOpen = (state) => {
    return _get(state, "navigation.isSharingMenuOpen", false);
};

/**
 * Is the nav bar open
 * @param state
 * @returns {*}
 */
export const getIsNavBarOpen = (state) => {
    return isMobile() ? _get(state, "navigation.navBarOpen", false) : true;
};

/**
 * Return the progress timer object
 * @param state
 * @returns {*}
 */
export const getProgressTimer = (state) => {
    return _get(state, "navigation.progressTracker", null);
};
